import { useState, useCallback } from "react";
import { updateCartQuantity, dispatchCartUpdates } from "@/shared/utils/cartOperations";
import getCartData from "@/shared/utils/getCartData";

interface SessionToRemove {
  sessionId: string;
  variantTitle: string;
  productTitle: string;
  trackKey: string;
  allSessionsForVariant: any[];
}

interface UseSessionManagementProps {
  cartData: any[];
  productRenderedLocation: string;
  headlessCartInfo: any;
  membershipInfo: any;
  isCustomerLogged: boolean;
  isCustomerActiveSubscriber: boolean;
  dispatch: any;
  trackSelections: {
    mainTrackSelection: any;
    battleKartSelection: any;
    miniTrackSection: any;
    juniorTrackSelection: any;
    intermediateTrackSelection: any;
  };
}

export const useSessionManagement = ({
  cartData,
  productRenderedLocation,
  headlessCartInfo,
  membershipInfo,
  isCustomerLogged,
  isCustomerActiveSubscriber,
  dispatch,
  trackSelections,
}: UseSessionManagementProps) => {
  const [selectedSessionId, setSelectedSessionId] = useState<string | null>(null);
  const [showRemoveConfirmation, setShowRemoveConfirmation] = useState(false);
  const [sessionToRemove, setSessionToRemove] = useState<SessionToRemove | null>(null);
  const [isRemoving, setIsRemoving] = useState(false);

  const handleRemoveSessionClick = useCallback((sessionId: string, variantTitle: string, productTitle: string, trackKey: string, allSessionsForVariant: any[]) => {
    const specificSession = allSessionsForVariant.find((session) => session.variantsSelectedObject.id === sessionId);
    setSessionToRemove({
      sessionId,
      variantTitle,
      productTitle,
      trackKey,
      allSessionsForVariant: specificSession ? [specificSession] : [],
    });
    setShowRemoveConfirmation(true);
  }, []);

  const handleConfirmRemove = useCallback(async () => {
    if (!sessionToRemove || sessionToRemove.allSessionsForVariant.length === 0) return;

    setIsRemoving(true);

    try {
      const session = sessionToRemove.allSessionsForVariant[0];
      const variantId = session.variantsSelectedObject.id;

      if (productRenderedLocation === "external") {
        // For headless cart
        const headlessItem = cartData.find((item: any) => item.merchandise?.id === variantId);
        if (headlessItem) {
          const result = await updateCartQuantity([headlessItem.id], 0, productRenderedLocation, headlessCartInfo);
          dispatchCartUpdates(dispatch, result.cart, productRenderedLocation, membershipInfo, {
            isCustomerLogged,
            isCustomerActiveSubscriber,
          });
        }
      } else {
        // For Shopify cart
        const adminId = variantId.replace("gid://shopify/ProductVariant/", "");
        const shopifyItem = cartData.find((item: any) => item.variant_id?.toString() === adminId || item.id?.toString() === adminId);
        const productVariantsIds = [adminId];

        if (shopifyItem) {
          const isItemIsMembershipProduct = shopifyItem.product_title?.toLowerCase().includes("membership");

          // Handle membership products
          if (isItemIsMembershipProduct && membershipInfo.eligibleVariants.length > 0) {
            const freshCartData = await getCartData(productRenderedLocation, headlessCartInfo);
            let lineItems: any[] = [];

            if (freshCartData) {
              const { cart } = freshCartData;
              if (cart) {
                const { items } = cart;
                if (items && items.length > 0) {
                  lineItems = items;
                }
              }
            }

            const memberVariants = lineItems
              .filter((line) => line.product_type === "Main Track")
              .map((item: any) => (item.title.toLowerCase().includes("member") ? item : null))
              .filter((item: any) => item !== null);

            const memberIds = memberVariants.map((item: any) => item.key || item.id);
            productVariantsIds.push(...memberIds);
          }

          // Only perform the cart removal — do NOT pass trackType/selectedVariants/dispatch
          // to prevent updateShopifyCart from overwriting Redux selectedVariants state.
          // The Redux state update is handled separately below (lines 148+) which correctly
          // preserves "selected but not yet in cart" variants.
          const result = await updateCartQuantity(productVariantsIds, 0, productRenderedLocation, headlessCartInfo);

          dispatchCartUpdates(dispatch, result.cart, productRenderedLocation, membershipInfo, {
            isCustomerLogged,
            isCustomerActiveSubscriber,
          });
        }
      }

      // Update Redux state
      const removedVariantId = session.variantsSelectedObject.id;
      const trackKey = sessionToRemove.trackKey;

      if (trackKey === "MAIN") {
        const updatedVariants = trackSelections.mainTrackSelection.variantsAndDate.selectedVariants.filter((v: any) => v.variantsSelectedObject.id !== removedVariantId);
        dispatch({
          type: "SET_DATE_AND_VARIANT",
          payload: {
            variantsAndDate: {
              ...trackSelections.mainTrackSelection.variantsAndDate,
              selectedVariants: updatedVariants,
            },
          },
        });

        // Update membership info
        const updatedMembershipInfo = membershipInfo?.eligibleVariants ? membershipInfo.eligibleVariants.filter((v: any) => v.variantsSelectedObject.id !== removedVariantId) : [];
        dispatch({
          type: "SET_MEMBERSHIP_INFO",
          payload: {
            membershipInfo: { ...membershipInfo, eligibleVariants: updatedMembershipInfo },
          },
        });
      } else if (trackKey === "BK") {
        const updatedVariants = trackSelections.battleKartSelection.variantsAndDate.selectedVariants.filter((v: any) => v.variantsSelectedObject.id !== removedVariantId);
        dispatch({
          type: "SET_DATE_AND_VARIANT_BATTLE_KART_TRACK",
          payload: {
            variantsAndDate: {
              ...trackSelections.battleKartSelection.variantsAndDate,
              selectedVariants: updatedVariants,
            },
          },
        });
      } else if (trackKey === "MINI") {
        const updatedVariants = trackSelections.miniTrackSection.variantsAndDate.selectedVariants.filter((v: any) => v.variantsSelectedObject.id !== removedVariantId);
        dispatch({
          type: "SET_DATE_AND_VARIANT_MINI_TRACK",
          payload: {
            variantsAndDate: {
              ...trackSelections.miniTrackSection.variantsAndDate,
              selectedVariants: updatedVariants,
            },
          },
        });
      } else if (trackKey === "JUNIOR") {
        const updatedVariants = trackSelections.juniorTrackSelection.variantsAndDate.selectedVariants.filter((v: any) => v.variantsSelectedObject.id !== removedVariantId);
        dispatch({
          type: "SET_DATE_AND_VARIANT_JUNIOR_TRACK",
          payload: {
            variantsAndDate: {
              ...trackSelections.juniorTrackSelection.variantsAndDate,
              selectedVariants: updatedVariants,
            },
          },
        });
      } else if (trackKey === "INTERMEDIATE") {
        const updatedVariants = trackSelections.intermediateTrackSelection.variantsAndDate.selectedVariants.filter((v: any) => v.variantsSelectedObject.id !== removedVariantId);
        dispatch({
          type: "SET_DATE_AND_VARIANT_INTERMEDIATE_TRACK",
          payload: {
            variantsAndDate: {
              ...trackSelections.intermediateTrackSelection.variantsAndDate,
              selectedVariants: updatedVariants,
            },
          },
        });
      }

      // Clear selection if removed
      if (selectedSessionId === sessionToRemove.sessionId) {
        setSelectedSessionId(null);
      }

      // Refresh cart for Shopify
      if (productRenderedLocation !== "external") {
        setTimeout(() => {
          dispatch({
            type: "SET_IS_SHOPIFY_CART_VARIANTS_LOADING",
            payload: { isShopifyCartVariantsLoaded: false },
          });
        }, 100);
      }

      setIsRemoving(false);
      setShowRemoveConfirmation(false);
      setSessionToRemove(null);
    } catch (error) {
      console.error("Failed to remove session:", error);
      setIsRemoving(false);
      setShowRemoveConfirmation(false);
      setSessionToRemove(null);
    }
  }, [
    sessionToRemove,
    productRenderedLocation,
    cartData,
    headlessCartInfo,
    dispatch,
    membershipInfo,
    isCustomerLogged,
    isCustomerActiveSubscriber,
    trackSelections,
    selectedSessionId,
  ]);

  const handleCancelRemove = useCallback(() => {
    setShowRemoveConfirmation(false);
    setSessionToRemove(null);
  }, []);

  return {
    selectedSessionId,
    setSelectedSessionId,
    showRemoveConfirmation,
    sessionToRemove,
    isRemoving,
    handleRemoveSessionClick,
    handleConfirmRemove,
    handleCancelRemove,
  };
};
